import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import supabase from '../../supabase';
import TopBar from '../../components/TopBar';
import { 
  FiDollarSign, 
  FiPackage, 
  FiUsers, 
  FiTrendingUp, 
  FiShoppingCart,
  FiMapPin,
  FiCreditCard,
  FiActivity
} from 'react-icons/fi';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import { useAuth } from '../../contexts/AuthContext';

const Statistics = () => {
  const { branchId, isAdmin } = useBranchFilter();
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState('week');
  const [stats, setStats] = useState({
    sales: { total: 0, trend: 0, data: [] },
    products: { total: 0, trend: 0, data: [] },
    customers: { total: 0, trend: 0, data: [] },
    auctions: { total: 0, trend: 0, data: [] },
    bids: { total: 0, totalAmount: 0, trend: 0, data: [] },
    branches: { total: 0, data: [] },
    bankAccounts: { total: 0, verified: 0, data: [] },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, [timeRange, branchId]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const since = new Date();
      if (timeRange === 'month') since.setDate(since.getDate() - 30); 
      else if (timeRange === 'year') since.setDate(since.getDate() - 365); 
      else since.setDate(since.getDate() - 7);

      // Fetch all data in parallel with branch filtering for branch managers
      // For products and auctions: get TOTAL count AND time-filtered data for charts
      let productsQueryTotal = supabase.from('products').select('id, created_at, branch_id');
      let productsQueryFiltered = supabase.from('products').select('id, created_at, branch_id').gte('created_at', since.toISOString());
      let auctionsQueryTotal = supabase.from('auction_with_branch').select('id, auction_date, branch_id');
      let auctionsQueryFiltered = supabase.from('auction_with_branch').select('id, auction_date, branch_id').gte('auction_date', since.toISOString());
      
      // Apply branch filter for branch managers
      if (branchId) {
        productsQueryTotal = productsQueryTotal.eq('branch_id', branchId);
        productsQueryFiltered = productsQueryFiltered.eq('branch_id', branchId);
        auctionsQueryTotal = auctionsQueryTotal.eq('branch_id', branchId);
        auctionsQueryFiltered = auctionsQueryFiltered.eq('branch_id', branchId);
      }
      
      const [
        { data: payments },
        { data: productsTotal },
        { data: productsFiltered },
        { data: users },
        { data: auctionsTotal },
        { data: auctionsFiltered },
        { data: bids },
        { data: branches },
        { data: bankAccounts }
      ] = await Promise.all([
        supabase.from('payments').select('amount, created_at').gte('created_at', since.toISOString()),
        productsQueryTotal,
        productsQueryFiltered,
        supabase.from('user_details').select('id, created_at').gte('created_at', since.toISOString()),
        auctionsQueryTotal,
        auctionsQueryFiltered,
        supabase.from('bids').select('amount, placed_at').gte('placed_at', since.toISOString()),
        isAdmin ? supabase.from('branch').select('id, created_at') : Promise.resolve({ data: [] }),
        supabase.from('user_bank_accounts').select('id, is_verified, created_at').gte('created_at', since.toISOString())
      ]);

      // Helper function to group data by day
      const bucketize = (rows, getVal, dateField = 'created_at') => {
        const byDay = new Map();
        for (const r of rows || []) {
          const day = new Date(r[dateField]).toISOString().slice(0, 10);
          byDay.set(day, (byDay.get(day) || 0) + (getVal ? getVal(r) : 1));
        }
        return [...byDay.entries()]
          .sort((a,b)=>a[0].localeCompare(b[0]))
          .map(([date, value]) => ({ date, value }));
      };

      // Calculate totals and trends
      const salesTotal = (payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
      const bidsTotal = (bids || []).length;
      const bidsAmount = (bids || []).reduce((s, b) => s + (Number(b.amount) || 0), 0);
      const verifiedAccounts = (bankAccounts || []).filter(a => a.is_verified).length;

      const salesData = bucketize(payments, (r) => Number(r.amount) || 0);
      const productData = bucketize(productsFiltered); // Chart uses filtered data
      const customerData = bucketize(users);
      const auctionData = bucketize(auctionsFiltered, null, 'auction_date'); // Chart uses filtered data
      const bidData = bucketize(bids, null, 'placed_at');
      const bankAccountData = bucketize(bankAccounts);

      setStats({
        sales: { total: salesTotal, trend: 0, data: salesData },
        products: { total: (productsTotal || []).length, trend: 0, data: productData }, // Total count, filtered chart
        customers: { total: (users || []).length, trend: 0, data: customerData },
        auctions: { total: (auctionsTotal || []).length, trend: 0, data: auctionData }, // Total count, filtered chart
        bids: { total: bidsTotal, totalAmount: bidsAmount, trend: 0, data: bidData },
        branches: { total: (branches || []).length, data: [] },
        bankAccounts: { total: (bankAccounts || []).length, verified: verifiedAccounts, data: bankAccountData },
      });
    } catch (error) {
      toast.error('Failed to fetch statistics');
      console.error('Statistics fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderSimpleChart = (data, color = 'blue') => {
    if (!data || data.length === 0) {
      return (
        <div className="h-32 flex items-center justify-center text-gray-400 text-sm">
          No data available
        </div>
      );
    }
    const maxValue = Math.max(...data.map(d => d.value), 1);
    const colorMap = {
      blue: { light: 'bg-blue-100', hover: 'hover:bg-blue-200', border: 'border-blue-400' },
      purple: { light: 'bg-purple-100', hover: 'hover:bg-purple-200', border: 'border-purple-400' },
      green: { light: 'bg-green-100', hover: 'hover:bg-green-200', border: 'border-green-400' },
      orange: { light: 'bg-orange-100', hover: 'hover:bg-orange-200', border: 'border-orange-400' },
      red: { light: 'bg-red-100', hover: 'hover:bg-red-200', border: 'border-red-400' },
      teal: { light: 'bg-teal-100', hover: 'hover:bg-teal-200', border: 'border-teal-400' },
    };
    const colors = colorMap[color] || colorMap.blue;
    
    return (
      <div className="h-32 flex items-end space-x-1 border-b border-l border-gray-200 pb-2 pl-2">
        {data.map((point, index) => (
          <div
            key={index}
            className={`flex-1 ${colors.light} ${colors.hover} transition-all rounded-t relative group cursor-pointer`}
            style={{
              height: `${(point.value / maxValue) * 100}%`,
              minHeight: '4px'
            }}
          >
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
              {new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: {point.value.toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  const renderPercentageBadge = (percentage) => {
    const isPositive = percentage >= 0;
    return (
      <div className={`flex items-center text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        <span className="mr-1">{isPositive ? '↑' : '↓'}</span>
        <span>{Math.abs(percentage)}%</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <TopBar />
        <div className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar />
      <div className="p-6 pb-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isAdmin ? 'Business Analytics' : `${user?.branch?.name || 'Branch'} Analytics`}
              </h1>
              <p className="text-gray-500 mt-1">
                {isAdmin ? 'Track your key metrics and performance indicators' : 'Track your branch metrics and performance'}
              </p>
            </div>
            <div className="flex space-x-2 mt-4 md:mt-0">
              <button
                onClick={() => setTimeRange('week')}
                className={`px-6 py-2 rounded-md font-medium transition-all ${
                  timeRange === 'week' 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                7 Days
              </button>
              <button
                onClick={() => setTimeRange('month')}
                className={`px-6 py-2 rounded-md font-medium transition-all ${
                  timeRange === 'month' 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                30 Days
              </button>
              <button
                onClick={() => setTimeRange('year')}
                className={`px-6 py-2 rounded-md font-medium transition-all ${
                  timeRange === 'year' 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                1 Year
              </button>
            </div>
          </div>
        </div>

        {/* Key Performance Indicators */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Revenue KPI */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Revenue</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">₹{stats.sales.total.toLocaleString()}</p>
                <div className="flex items-center mt-2">
                  {renderPercentageBadge(stats.sales.trend)}
                  <span className="text-xs text-gray-500 ml-2">vs last period</span>
                </div>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <FiDollarSign className="text-2xl text-blue-600" />
              </div>
            </div>
          </div>

          {/* Products KPI */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Products</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.products.total}</p>
                <div className="flex items-center mt-2">
                  {renderPercentageBadge(stats.products.trend)}
                  <span className="text-xs text-gray-500 ml-2">new additions</span>
                </div>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <FiPackage className="text-2xl text-purple-600" />
              </div>
            </div>
          </div>

          {/* Customers KPI */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Customers</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.customers.total}</p>
                <div className="flex items-center mt-2">
                  {renderPercentageBadge(stats.customers.trend)}
                  <span className="text-xs text-gray-500 ml-2">new signups</span>
                </div>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <FiUsers className="text-2xl text-green-600" />
              </div>
            </div>
          </div>

          {/* Auctions KPI */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Auctions</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.auctions.total}</p>
                <div className="flex items-center mt-2">
                  {renderPercentageBadge(stats.auctions.trend)}
                  <span className="text-xs text-gray-500 ml-2">total events</span>
                </div>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <FiShoppingCart className="text-2xl text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Sales Trend */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">Sales Trend</h3>
                <FiDollarSign className="text-blue-600" />
              </div>
              <div className="flex items-baseline space-x-2">
                <span className="text-2xl font-bold text-gray-900">₹{stats.sales.total.toLocaleString()}</span>
                <span className="text-sm text-gray-500">in {timeRange}</span>
              </div>
            </div>
            {renderSimpleChart(stats.sales.data, 'blue')}
            <div className="mt-4 text-xs text-gray-500 text-center">
              {stats.sales.data.length} data points
            </div>
          </div>

          {/* Product Growth */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">Product Growth</h3>
                <FiPackage className="text-purple-600" />
              </div>
              <div className="flex items-baseline space-x-2">
                <span className="text-2xl font-bold text-gray-900">{stats.products.total}</span>
                <span className="text-sm text-gray-500">products added</span>
              </div>
            </div>
            {renderSimpleChart(stats.products.data, 'purple')}
            <div className="mt-4 text-xs text-gray-500 text-center">
              {stats.products.data.length} data points
            </div>
          </div>

          {/* Customer Acquisition */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">Customer Acquisition</h3>
                <FiUsers className="text-green-600" />
              </div>
              <div className="flex items-baseline space-x-2">
                <span className="text-2xl font-bold text-gray-900">{stats.customers.total}</span>
                <span className="text-sm text-gray-500">new customers</span>
              </div>
            </div>
            {renderSimpleChart(stats.customers.data, 'green')}
            <div className="mt-4 text-xs text-gray-500 text-center">
              {stats.customers.data.length} data points
            </div>
          </div>

          {/* Bidding Activity */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">Bidding Activity</h3>
                <FiActivity className="text-orange-600" />
              </div>
              <div className="flex items-baseline space-x-2">
                <span className="text-2xl font-bold text-gray-900">{stats.bids.total}</span>
                <span className="text-sm text-gray-500">total bids (₹{stats.bids.totalAmount.toLocaleString()})</span>
              </div>
            </div>
            {renderSimpleChart(stats.bids.data, 'orange')}
            <div className="mt-4 text-xs text-gray-500 text-center">
              {stats.bids.data.length} data points
            </div>
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">System Overview</h3>
          <div className={`grid grid-cols-1 ${isAdmin ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
            {/* Branches - Admin Only */}
            {isAdmin && (
              <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                <div className="p-3 bg-indigo-100 rounded-lg flex-shrink-0">
                  <FiMapPin className="text-xl text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Store Branches</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stats.branches.total}</p>
                  <p className="text-xs text-gray-500 mt-1">Active locations</p>
                </div>
              </div>
            )}

            {/* Bank Accounts */}
            <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="p-3 bg-teal-100 rounded-lg flex-shrink-0">
                <FiCreditCard className="text-xl text-teal-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Bank Accounts</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.bankAccounts.total}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.bankAccounts.verified} verified ({((stats.bankAccounts.verified / (stats.bankAccounts.total || 1)) * 100).toFixed(0)}%)
                </p>
              </div>
            </div>

            {/* Bank Account Chart */}
            <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="p-3 bg-orange-100 rounded-lg flex-shrink-0">
                <FiTrendingUp className="text-xl text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">New Accounts</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.bankAccounts.total}</p>
                <p className="text-xs text-gray-500 mt-1">Last {timeRange}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics; 